/* eslint-disable max-lines */
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  createFilterOptions,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import LeadGenerationContext from '../../context/LeadGenerationContext';
import { BusinessAditionalInfoStyles as classes } from './BusinessAditionalInfoStyles';
import { CustomInput } from '../LeadGeneration/CustomInput/CustomInput';
import { CustomSelect } from '../LeadGeneration/CustomSelect/CustomSelect';
import { autoCompleteCommonCommercial, commonTernary } from '../commonCode';
import {
  ADDITIONAL_TITLE,
  NET_WORTH_KEY1,
  NO_EMP_KEY1,
  NO_EMP_KEY2,
  NO_EMP_KEY3,
  NO_EMP_KEY4,
  NO_EMP_KEY5,
  NET_WORTH_KEY2,
  NET_WORTH_KEY3,
  NET_WORTH_KEY4,
  LESS_THAN_500000,
  ANNUAL_SALES_KEY1,
  ANNUAL_SALES_KEY2,
  ANNUAL_SALES_KEY3,
  NOTNA,
  NET_WORTH_KEY5,
  NET_WORTH_KEY6,
  ADDITIONAL_HEAD,
  BUSINESS_TAX_ID,
  BUSINESS_TAX_ID_LABEL,
  WEBSITE_LABEL,
  WEBSITE_PLACEHOLDER,
  ANNUAL_SALES,
  REQUIRED,
  NUMBER_OF_EMP,
  NET_WORTH,
  EN,
  ES,
} from '../../constants';
import Layout from '../Layout';
import { productTypeMapping } from '../../services/productTypeMapping';
import { commonUtility } from '../LeadGeneration/UtilityFunctions';
import ComercialImage2 from '../../images/Comercial_Image_2.png';
import { productNameURLMapping } from '../../services/productNameURLMapping';
import { industryTypeListEN, industryTypeListES } from './IndustryTypeList';
import { CommonStyles } from '../LeadGeneration/CommonStyles';
import SaveAppContactInfo from '../SaveApp/SaveAppContactInfo';
import SaveContinueModal from '../Modal/SaveContinueModal';

const AdditionalInfo = (props) => {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const [industryType, setIndustryType] = useState([]);
  const {
    theme,
    businessAdditionalInfo,
    businessAdditionalInfoErrors,
    productType,
    businessContactInformation,
    productName,
    setIsModalSaveLater,
    isModalSaveLater,
    isModalSave,
  } = leadGenerationContext;
  const commonClass = CommonStyles(theme);
  const { handleChange, handleBlur, handleFocus, handleContinue, handleBack, isValidating } = props;

  useEffect(() => {
    if (leadGenerationContext.language === EN) {
      setIndustryType(industryTypeListEN());
    }
    if (leadGenerationContext.language === ES) {
      setIndustryType(industryTypeListES());
    }
  }, [leadGenerationContext.language]);

  const filterOptionsIndustry = createFilterOptions({
    matchFrom: 'start',
    stringify: (option) => option,
  });

  const handleAutoCompleteInd = (name, value) => {
    autoCompleteCommonCommercial(name, value, leadGenerationContext);
  };

  const stateDepartmentRegistryJSX = () => {
    if (
      businessContactInformation?.businessType !== 'DBA' &&
      businessContactInformation?.businessType !== ''
    ) {
      return (
        <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
          {/* <Grid  size={{ xs: 12 }}> */}
          <CustomInput
            id="stateDeptRegistry"
            dataTestId="state-dept-registry-id"
            inputLabelAriaLabel={t('State Department Registry #', {
              defaultValue: 'State Department Registry #',
            })}
            label={t('State Department Registry #', {
              defaultValue: 'State Department Registry #',
            })}
            handleChange={handleChange}
            placeholder={t('Enter State Department Registry', {
              defaultValue: 'Enter State Department Registry',
            })}
            fullWidth
            inputProps={{ maxLength: 20, 'data-testid': 'state-dept-registry' }}
            inputMode="numeric"
            value={commonUtility(businessAdditionalInfo?.stateDeptRegistry)}
            error={commonUtility(businessAdditionalInfoErrors?.stateDeptRegistry)}
          />

          <Box
            component="span"
            sx={classes.error_message}
            role="alert"
            data-testid="state-dept-registry-error"
          >
            {t(businessAdditionalInfoErrors.stateDeptRegistry, {
              defaultValue: businessAdditionalInfoErrors.stateDeptRegistry,
            })}
          </Box>

          {/* </Grid> */}
        </Grid>
      );
    }
  };

  const industryTypeJSX = () => (
    <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
      <InputLabel
        htmlFor="industryType"
        required
        id="industryType_label"
        data-testid="industryType_testid"
        aria-label={`${t('Industry Type', { defaultValue: 'Industry Type' })} ${t(REQUIRED, {
          defaultValue: REQUIRED,
        })}`}
        sx={commonClass.label_title}
      >
        {t('Industry Type', { defaultValue: 'Industry Type' })}
      </InputLabel>
      <Autocomplete
        id="industryType"
        data-testid="autocomplete"
        name="industryType"
        options={sortedIndustryTypeList1}
        getOptionLabel={(option) => option}
        value={commonUtility(leadGenerationContext.businessAdditionalInfo?.industryType)}
        onChange={(_event, selectedValue) => {
          handleAutoCompleteInd('industryType', selectedValue);
        }}
        renderInput={(params) => (
          <OutlinedInput
            id="industryType"
            name="industryType"
            inputProps={params.inputProps}
            ref={params.InputProps.ref}
            slotProps={{
              notchedOutline: {
                sx: !leadGenerationContext.businessAdditionalInfoErrors.industryType
                  ? commonClass.notchedOutline
                  : commonClass.notchedOutlineErr,
              },
            }}
            sx={commonClass.outlined_inputs}
            placeholder={t('Select business industry type', {
              defaultValue: 'Select business industry type',
            })}
            fullWidth
            value={commonUtility(leadGenerationContext.businessAdditionalInfo?.industryType)}
          />
        )}
        filterOptions={filterOptionsIndustry}
      />

      <Box
        component="span"
        sx={classes.error_message}
        role="alert"
        data-testid="industry-type-error"
      >
        {t(leadGenerationContext.businessAdditionalInfoErrors?.industryType, {
          defaultValue: leadGenerationContext.businessAdditionalInfoErrors?.industryType,
        })}
      </Box>
    </Grid>
  );

  const optionValueCreditRequest = () => {
    let optionValue = [];
    if (productNameURLMapping(productName) === 'Prestamo-Comercial') {
      optionValue = [
        'Acquisition of property or operational equipment',
        'Business enhancements',
        'Operating capital',
        'Refinancing',
        'Construction',
        'Business in operation purchase',
        'Other',
      ];
    } else if (productNameURLMapping(productName) === 'Linea-de-Credito-Comercial') {
      optionValue = [
        'Inventory acquisition (Recurring need)',
        'Accounts receivable financing',
        'Accounts payable',
        'Other',
      ];
    } else if (productNameURLMapping(productName) === 'Tarjeta-de-Credito-Comercial') {
      optionValue = ['Online shopping', 'Entertainment expenses', 'Other'];
    }
    return optionValue;
  };
  const handleSaveApplication = (e) => {
    e.preventDefault();
    setIsModalSaveLater(true);
  };

  const sortedIndustryTypeList1 = [...industryType].sort((a, b) => a.localeCompare(b));

  return (
    <Layout
      handleSaveApplication={handleSaveApplication}
      ariaDescribedby="BusinessAdditionalInfoHeading"
      headingId="BusinessAdditionalInfoHeading"
      ariaDescribedbyId="bussinessAdditionalInfoDesc"
      classes={classes}
      descriptionKey={ADDITIONAL_HEAD}
      descriptionValue={ADDITIONAL_HEAD}
      stepValue={1}
      handleBack={handleBack}
      handleContinue={handleContinue}
      disableCondition={isValidating}
      backBtnTestid="back_button_testid"
      continueBtnTestid="continue_testId"
      screenLoading={isValidating}
      layoutImage={ComercialImage2}
    >
      <Box component="div" sx={classes.business_info_container}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Typography
              sx={classes.business_info_heading}
              id="aboutBusinessHeading"
              variant="h2"
              aria-label="More about your business"
            >
              {t(ADDITIONAL_TITLE, {
                defaultValue: ADDITIONAL_TITLE,
              })}
            </Typography>
          </Grid>
        </Grid>

        <Grid container>
          <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
            <CustomInput
              id="businessTaxId"
              dataTestId="businessTaxId_testid"
              required
              inputLabelAriaLabel={`${t(BUSINESS_TAX_ID_LABEL, {
                defaultValue: BUSINESS_TAX_ID_LABEL,
              })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
              label={t(BUSINESS_TAX_ID_LABEL, { defaultValue: BUSINESS_TAX_ID_LABEL })}
              autoFocus
              handleChange={handleChange}
              placeholder={t(BUSINESS_TAX_ID, {
                defaultValue: BUSINESS_TAX_ID,
              })}
              fullWidth
              inputProps={{
                maxLength: 9,
                inputMode: 'numeric',
              }}
              value={commonUtility(businessAdditionalInfo?.businessTaxId)}
              error={commonUtility(businessAdditionalInfoErrors?.businessTaxId)}
            />

            <Box
              component="span"
              sx={classes.error_message}
              role="alert"
              data-testid="first-name-error"
            >
              {t(businessAdditionalInfoErrors.businessTaxId, {
                defaultValue: businessAdditionalInfoErrors.businessTaxId,
              })}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
            <CustomInput
              id="website"
              dataTestId="website_testid"
              inputLabelAriaLabel={t(WEBSITE_LABEL, {
                defaultValue: WEBSITE_LABEL,
              })}
              label={t(WEBSITE_LABEL, { defaultValue: WEBSITE_LABEL })}
              handleChange={handleChange}
              placeholder={t(WEBSITE_PLACEHOLDER, {
                defaultValue: WEBSITE_PLACEHOLDER,
              })}
              fullWidth
              inputProps={{
                maxLength: 100,
              }}
              value={commonUtility(businessAdditionalInfo?.website)}
              error={commonUtility(businessAdditionalInfoErrors?.website)}
            />

            <Box component="span" sx={classes.error_message} role="alert">
              {t(businessAdditionalInfoErrors.website, {
                defaultValue: businessAdditionalInfoErrors.website,
              })}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
            <CustomSelect
              id="annualSales"
              dataTestId="annualSales_testid"
              required
              inputLabelAriaLabel={`${t(ANNUAL_SALES, {
                defaultValue: ANNUAL_SALES,
              })} `}
              label={t(ANNUAL_SALES, { defaultValue: ANNUAL_SALES })}
              options={
                productTypeMapping(productType) !== 'Credit'
                  ? [
                      LESS_THAN_500000,
                      ANNUAL_SALES_KEY1,
                      ANNUAL_SALES_KEY2,
                      ANNUAL_SALES_KEY3,
                      NOTNA,
                    ]
                  : [
                      '$0 - $500,000',
                      '$500,001 - $2,500,000',
                      '$2,500,001 - $5,000,000',
                      '$5,000,001 - $10,000,000',
                      '$10,000,001 - $25,000,000',
                      'Over $25,000,000',
                    ]
              }
              handleChange={handleChange}
              fullWidth
              selectAriaLabel={`${t('Aria Label Annual Sales', {
                defaultValue: 'Annual Sales Required Select Annual Sales dropdown menu button',
              })} `}
              value={commonUtility(businessAdditionalInfo?.annualSales)}
              error={commonUtility(businessAdditionalInfoErrors?.annualSales)}
            />

            <Box component="span" sx={classes.error_message} role="alert">
              {t(businessAdditionalInfoErrors.annualSales, {
                defaultValue: businessAdditionalInfoErrors.annualSales,
              })}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
            <CustomSelect
              id="netWorth"
              dataTestId="netWorth_testid"
              required
              inputLabelAriaLabel={`${t(NET_WORTH, {
                defaultValue: NET_WORTH,
              })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
              label={t(NET_WORTH, { defaultValue: NET_WORTH })}
              options={[
                NET_WORTH_KEY1,
                NET_WORTH_KEY2,
                NET_WORTH_KEY3,
                NET_WORTH_KEY4,
                NET_WORTH_KEY5,
                NET_WORTH_KEY6,
              ]}
              handleChange={handleChange}
              fullWidth
              value={commonUtility(businessAdditionalInfo?.netWorth)}
              error={commonUtility(businessAdditionalInfoErrors?.netWorth)}
              selectAriaLabel={`${t('Aria Label Net Worth', {
                defaultValue: 'Net Worth Required Select Net Worth dropdown menu button',
              })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
            />

            <Box component="span" sx={classes.error_message} role="alert">
              {t(businessAdditionalInfoErrors.netWorth, {
                defaultValue: businessAdditionalInfoErrors.netWorth,
              })}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
            <CustomSelect
              id="noOfEmployees"
              dataTestId="noOfEmployees_testid"
              required
              inputLabelAriaLabel={`${t(NUMBER_OF_EMP, {
                defaultValue: NUMBER_OF_EMP,
              })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
              label={t(NUMBER_OF_EMP, { defaultValue: NUMBER_OF_EMP })}
              options={[NO_EMP_KEY1, NO_EMP_KEY2, NO_EMP_KEY3, NO_EMP_KEY4, NO_EMP_KEY5]}
              handleChange={handleChange}
              fullWidth
              value={commonUtility(businessAdditionalInfo?.noOfEmployees)}
              error={commonUtility(businessAdditionalInfoErrors?.noOfEmployees)}
              selectAriaLabel={`${t('Aria Label Number of Employees', {
                defaultValue:
                  'Number of Employees Required Select Number of Employees dropdown menu button',
              })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
            />

            <Box
              component="span"
              sx={classes.error_message}
              role="alert"
              data-testid="noemp_errorid"
            >
              {t(businessAdditionalInfoErrors.noOfEmployees, {
                defaultValue: businessAdditionalInfoErrors.noOfEmployees,
              })}
            </Box>
          </Grid>
          {productTypeMapping(productType) === 'Credit' ? (
            <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
              <CustomSelect
                id="timeInBusiness"
                dataTestId="timeInBusiness_testid"
                required
                inputLabelAriaLabel={`${t('Time in business', {
                  defaultValue: 'Time in business',
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                label={t('Time in business', { defaultValue: 'Time in business' })}
                options={[
                  '0 - 12 months',
                  '1 - 2 years',
                  '3 - 5 years',
                  '6 - 9 years',
                  '+10 years',
                ]}
                handleChange={handleChange}
                fullWidth
                value={commonUtility(businessAdditionalInfo?.timeInBusiness)}
                error={commonUtility(businessAdditionalInfoErrors?.timeInBusiness)}
                selectAriaLabel={`${t('Aria Label Time in business', {
                  defaultValue:
                    'Time in business Required Select Time in business dropdown menu button',
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
              />

              <Box
                component="span"
                sx={classes.error_message}
                role="alert"
                data-testid="noemp_errorid"
              >
                {t(businessAdditionalInfoErrors.timeInBusiness, {
                  defaultValue: businessAdditionalInfoErrors.timeInBusiness,
                })}
              </Box>
            </Grid>
          ) : (
            ''
          )}
          {stateDepartmentRegistryJSX()}
          {industryTypeJSX()}
        </Grid>
        {productTypeMapping(productType) === 'Credit' ? (
          <div style={{ margin: '30px', border: '1px solid #E6E6E6' }}> </div>
        ) : (
          ''
        )}
        {productTypeMapping(productType) === 'Credit' ? (
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <Typography
                sx={classes.business_info_heading}
                id="aboutBusinessHeadingTwo"
                variant="h2"
                aria-label="Understanding your credit needs"
              >
                {t('Understanding your credit needs', {
                  defaultValue: 'Understanding your credit needs',
                })}
              </Typography>
            </Grid>
          </Grid>
        ) : (
          ''
        )}
        {productTypeMapping(productType) === 'Credit' ? (
          <Grid container spacing={4} sx={classes.creditContainer_padding}>
            <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
              <CustomInput
                id="creditAmount"
                dataTestId="creditAmount_testid"
                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                required
                inputLabelAriaLabel={`${t('Credit Amount', {
                  defaultValue: 'Credit Amount',
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                label={t('Credit Amount', { defaultValue: 'Credit Amount' })}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleFocus={handleFocus}
                placeholder={t('Enter Credit Amount', {
                  defaultValue: 'Enter Credit Amount',
                })}
                fullWidth
                inputProps={{
                  maxLength: 10,
                  inputMode: 'numeric',
                }}
                value={commonUtility(businessAdditionalInfo?.creditAmount)}
                error={commonUtility(businessAdditionalInfoErrors?.creditAmount)}
              />

              <Box
                component="span"
                sx={classes.error_message}
                role="alert"
                data-testid="creditAmount-error"
              >
                {t(businessAdditionalInfoErrors.creditAmount, {
                  defaultValue: businessAdditionalInfoErrors.creditAmount,
                })}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
              <CustomSelect
                id="creditRequestReason"
                dataTestId="creditRequestReason_testid"
                required
                inputLabelAriaLabel={`${t('Reason for Credit Request', {
                  defaultValue: 'Reason for Credit Request',
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                label={t('Reason for Credit Request', {
                  defaultValue: 'Reason for Credit Request',
                })}
                options={optionValueCreditRequest()}
                handleChange={handleChange}
                fullWidth
                value={commonUtility(businessAdditionalInfo?.creditRequestReason)}
                error={commonUtility(businessAdditionalInfoErrors?.creditRequestReason)}
                selectAriaLabel={`${t('Aria Label credit request', {
                  defaultValue:
                    'credit request Required Select credit request dropdown menu button',
                })} `}
              />
              {/* <CustomInput
                id="creditRequestReason"
                dataTestId="creditRequestReason_testid"
                required
                inputLabelAriaLabel={`${t('Reason for Credit Request', {
                  defaultValue: 'Reason for Credit Request',
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                label={t('Reason for Credit Request', {
                  defaultValue: 'Reason for Credit Request',
                })}
                options={optionValueCreditRequest()}
                handleChange={handleChange}
                fullWidth
                value={commonUtility(businessAdditionalInfo?.creditRequestReason)}
                error={commonUtility(businessAdditionalInfoErrors?.creditRequestReason)}
              /> */}

              <Box
                component="span"
                sx={classes.error_message}
                role="alert"
                data-testid="creditRequestReason-error"
              >
                {t(businessAdditionalInfoErrors.creditRequestReason, {
                  defaultValue: businessAdditionalInfoErrors.creditRequestReason,
                })}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
              <CustomSelect
                id="collateralCredit"
                dataTestId="collateralCredit_testid"
                required
                inputLabelAriaLabel={`${t('Do you have a collateral for the credit?', {
                  defaultValue: 'Do you have a collateral for the credit?',
                })} `}
                label={t('Do you have a collateral for the credit?', {
                  defaultValue: 'Do you have a collateral for the credit?',
                })}
                options={['Yes', 'No']}
                handleChange={handleChange}
                fullWidth
                value={commonUtility(businessAdditionalInfo?.collateralCredit)}
                error={commonUtility(businessAdditionalInfoErrors?.collateralCredit)}
                // noPlaceHolderLabel
                selectAriaLabel={`${t('Aria Label Annual Sales', {
                  defaultValue: 'Annual Sales Required Select Annual Sales dropdown menu button',
                })} `}
              />

              <Box component="span" sx={classes.error_message} role="alert">
                {t(businessAdditionalInfoErrors.collateralCredit, {
                  defaultValue: businessAdditionalInfoErrors.collateralCredit,
                })}
              </Box>
            </Grid>
            {commonTernary(
              businessAdditionalInfo.collateralCredit === 'Yes',
              <Grid size={{ xs: 12, md: 6 }} sx={classes.field_padding}>
                <CustomSelect
                  id="specifyCollateral"
                  dataTestId="specifyCollateral_testid"
                  required
                  inputLabelAriaLabel={`${t('Specify Collateral', {
                    defaultValue: 'Specify Collateral',
                  })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                  label={t('Specify Collateral', { defaultValue: 'Specify Collateral' })}
                  options={[
                    'Cash (Saving Account)',
                    'Cash (Certificate Deposit)',
                    'Movable property (Existing)',
                    'Movable property (To be purchased)',
                    'Immovable property (Commercial)',
                    'Immovable property (Residential)',
                  ]}
                  handleChange={handleChange}
                  fullWidth
                  value={commonUtility(businessAdditionalInfo?.specifyCollateral)}
                  error={commonUtility(businessAdditionalInfoErrors?.specifyCollateral)}
                />
                {/* <CustomInput
                  id="specifyCollateral"
                  dataTestId="specifyCollateral_testid"
                  required
                  inputLabelAriaLabel={`${t('Specify Collateral', {
                    defaultValue: 'Specify Collateral',
                  })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                  label={t('Specify Collateral', { defaultValue: 'Specify Collateral' })}
                  options={[
                    'Cash (Saving Account)',
                    'Cash (Certificate Deposit)',
                    'Movable property (Existing)',
                    'Movable property (To be purchased)',
                    'Immovable property (Commercial)',
                    'Immovable property (Residential)',
                  ]}
                  handleChange={handleChange}
                  fullWidth
                  value={commonUtility(businessAdditionalInfo?.specifyCollateral)}
                  error={commonUtility(businessAdditionalInfoErrors?.specifyCollateral)}
                /> */}

                <Box component="span" sx={classes.error_message} role="alert">
                  {t(businessAdditionalInfoErrors.specifyCollateral, {
                    defaultValue: businessAdditionalInfoErrors.specifyCollateral,
                  })}
                </Box>
              </Grid>,
              ''
            )}
          </Grid>
        ) : (
          ''
        )}
      </Box>
      {isModalSave && <SaveContinueModal />}
      {isModalSaveLater && (
        <SaveAppContactInfo openModal={isModalSaveLater} currentPage="business-additional-info" />
      )}
    </Layout>
  );
};

export default AdditionalInfo;

AdditionalInfo.propTypes = {
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  handleFocus: PropTypes.func,
  handleBack: PropTypes.func,
  handleContinue: PropTypes.func,
  isValidating: PropTypes.bool,
};
